import { useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  ArrowLeft,
  Clock,
  CheckCircle2,
  Save,
  ChevronLeft,
  ChevronRight,
  PenTool,
} from 'lucide-react';
import { toast } from 'sonner';

interface StudentSubmission {
  studentId: string;
  studentName: string;
  avatar: string;
  score: number | null;
  comment: string;
  content: any;
  timeSpent: number;
  submittedAt: Date;
}

export default function ActivityGrading() {
  const { studentId, activityId } = useParams();
  const navigate = useNavigate();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState<number>(0);
  const [comment, setComment] = useState<string>('');

  // Mock data - would be filtered based on activityId in real app
  const [submissions] = useState<StudentSubmission[]>([
    {
      studentId: '1',
      studentName: 'Sarah Johnson',
      avatar: 'bg-blue-500',
      score: 85,
      comment: 'Great detail! Consider adding more labels.',
      content: {
        type: 'draw',
        imageUrl: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=600',
      },
      timeSpent: 300,
      submittedAt: new Date(2026, 1, 19, 11, 15),
    },
    {
      studentId: '2',
      studentName: 'Michael Chen',
      avatar: 'bg-green-500',
      score: null,
      comment: '',
      content: {
        type: 'draw',
        imageUrl: 'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=600',
      },
      timeSpent: 250,
      submittedAt: new Date(2026, 1, 19, 11, 20),
    },
    {
      studentId: '3',
      studentName: 'Emily Rodriguez',
      avatar: 'bg-purple-500',
      score: 90,
      comment: 'Excellent work! Very detailed.',
      content: {
        type: 'draw',
        imageUrl: 'https://images.unsplash.com/photo-1618005198919-d3d4b5a92ead?w=600',
      },
      timeSpent: 280,
      submittedAt: new Date(2026, 1, 19, 11, 25),
    },
    {
      studentId: '4',
      studentName: 'James Wilson',
      avatar: 'bg-orange-500',
      score: null,
      comment: '',
      content: {
        type: 'draw',
        imageUrl: 'https://images.unsplash.com/photo-1530587191325-3db32d826c18?w=600',
      },
      timeSpent: 290,
      submittedAt: new Date(2026, 1, 19, 11, 30),
    },
  ]);

  const currentSubmission = submissions[currentIndex];

  const activityInfo = {
    name: 'Draw-It: Photosynthesis Process',
    type: 'draw',
    icon: PenTool,
    maxScore: 100,
  };

  const handleSave = () => {
    toast.success(`Grade saved for ${currentSubmission.studentName}`);
    
    // Move to next ungraded submission
    const nextUngraded = submissions.findIndex(
      (s, idx) => idx > currentIndex && s.score === null
    );
    
    if (nextUngraded !== -1) {
      setCurrentIndex(nextUngraded);
      setScore(0);
      setComment('');
    } else if (currentIndex < submissions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setScore(submissions[currentIndex + 1].score || 0);
      setComment(submissions[currentIndex + 1].comment || '');
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setScore(submissions[currentIndex - 1].score || 0);
      setComment(submissions[currentIndex - 1].comment || '');
    }
  };

  const handleNext = () => {
    if (currentIndex < submissions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setScore(submissions[currentIndex + 1].score || 0);
      setComment(submissions[currentIndex + 1].comment || '');
    }
  };

  const gradedCount = submissions.filter(s => s.score !== null).length;

  const renderContent = () => {
    switch (currentSubmission.content.type) {
      case 'draw':
        return (
          <div className="space-y-4">
            <img
              src={currentSubmission.content.imageUrl}
              alt="Student drawing"
              className="w-full rounded-xl border-2 border-border shadow-lg"
            />
          </div>
        );
      
      case 'open':
        return (
          <div className="bg-muted/50 rounded-xl p-6 border border-border">
            <p className="text-foreground leading-relaxed text-lg">
              {currentSubmission.content.text}
            </p>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <>
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/gradebook/lesson-view')}
                className="rounded-xl"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Gradebook
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <activityInfo.icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-foreground">{activityInfo.name}</h1>
                  <p className="text-sm text-muted-foreground">
                    Grading {currentIndex + 1} of {submissions.length}
                  </p>
                </div>
              </div>
            </div>
            <Badge className="rounded-lg">
              {gradedCount} / {submissions.length} Graded
            </Badge>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content Area */}
          <div className="lg:col-span-2 space-y-6">
            {/* Student Info */}
            <div className="bg-card border border-border rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full ${currentSubmission.avatar} flex items-center justify-center text-white text-lg font-semibold`}>
                    {currentSubmission.studentName.charAt(0)}
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">
                      {currentSubmission.studentName}
                    </h2>
                    <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {Math.floor(currentSubmission.timeSpent / 60)}m {currentSubmission.timeSpent % 60}s
                      </div>
                      <span>•</span>
                      <span>{currentSubmission.submittedAt.toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                {currentSubmission.score !== null && (
                  <Badge className="bg-green-100 text-green-700 border-green-200 text-lg px-4 py-2">
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Graded
                  </Badge>
                )}
              </div>
            </div>

            {/* Submission Content */}
            <div className="bg-card border border-border rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">Student Submission</h3>
              {renderContent()}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={currentIndex === 0}
                className="rounded-xl"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>
              <div className="text-sm text-muted-foreground">
                {currentIndex + 1} of {submissions.length}
              </div>
              <Button
                variant="outline"
                onClick={handleNext}
                disabled={currentIndex === submissions.length - 1}
                className="rounded-xl"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>

          {/* Grading Panel */}
          <div className="lg:col-span-1">
            <div className="bg-card border border-border rounded-2xl p-6 sticky top-24">
              <h3 className="text-lg font-semibold text-foreground mb-6">Grade Submission</h3>
              
              <div className="space-y-6">
                {/* Score Input */}
                <div>
                  <Label htmlFor="score" className="text-base">
                    Score (out of {activityInfo.maxScore})
                  </Label>
                  <div className="flex items-center gap-3 mt-2">
                    <Input
                      id="score"
                      type="number"
                      min="0"
                      max={activityInfo.maxScore}
                      value={score}
                      onChange={(e) => setScore(Number(e.target.value))}
                      className="rounded-xl text-2xl font-bold text-center h-16"
                    />
                    <span className="text-2xl font-semibold text-muted-foreground">
                      / {activityInfo.maxScore}
                    </span>
                  </div>
                  
                  {/* Quick Score Buttons */}
                  <div className="grid grid-cols-4 gap-2 mt-3">
                    {[100, 90, 80, 70].map((quickScore) => (
                      <Button
                        key={quickScore}
                        variant="outline"
                        size="sm"
                        onClick={() => setScore(quickScore)}
                        className="rounded-lg"
                      >
                        {quickScore}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Rubric (Optional) */}
                <div className="bg-muted/50 rounded-xl p-4 space-y-2">
                  <p className="text-sm font-semibold text-foreground">Rubric Guide</p>
                  <div className="space-y-1 text-sm text-muted-foreground">
                    <div className="flex justify-between">
                      <span>90-100:</span>
                      <span>Exceptional</span>
                    </div>
                    <div className="flex justify-between">
                      <span>80-89:</span>
                      <span>Good</span>
                    </div>
                    <div className="flex justify-between">
                      <span>70-79:</span>
                      <span>Satisfactory</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Below 70:</span>
                      <span>Needs Work</span>
                    </div>
                  </div>
                </div>

                {/* Comment */}
                <div>
                  <Label htmlFor="comment" className="text-base">
                    Feedback (Optional)
                  </Label>
                  <Textarea
                    id="comment"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Add constructive feedback for the student..."
                    className="rounded-xl mt-2 min-h-[120px]"
                  />
                </div>

                {/* Save Button */}
                <Button
                  onClick={handleSave}
                  className="w-full rounded-xl h-12"
                  size="lg"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save & Next
                </Button>

                {/* Progress */}
                <div className="pt-4 border-t border-border">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-muted-foreground">Grading Progress</span>
                    <span className="font-semibold">
                      {Math.round((gradedCount / submissions.length) * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all"
                      style={{ width: `${(gradedCount / submissions.length) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Thumbnail Navigation */}
        <div className="mt-8 bg-card border border-border rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">All Submissions</h3>
          <ScrollArea className="w-full">
            <div className="flex gap-4 pb-4">
              {submissions.map((submission, idx) => (
                <button
                  key={submission.studentId}
                  onClick={() => {
                    setCurrentIndex(idx);
                    setScore(submission.score || 0);
                    setComment(submission.comment || '');
                  }}
                  className={`flex-shrink-0 w-40 rounded-xl border-2 overflow-hidden transition-all ${
                    currentIndex === idx
                      ? 'border-primary shadow-lg'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="aspect-video bg-muted relative">
                    <img
                      src={submission.content.imageUrl}
                      alt={submission.studentName}
                      className="w-full h-full object-cover"
                    />
                    {submission.score !== null && (
                      <div className="absolute top-2 right-2 w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="p-2 bg-card">
                    <p className="text-xs font-medium text-foreground truncate">
                      {submission.studentName}
                    </p>
                    {submission.score !== null ? (
                      <p className="text-xs text-green-600 font-semibold">{submission.score}/100</p>
                    ) : (
                      <p className="text-xs text-orange-600">Not graded</p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </ScrollArea>
        </div>
      </div>
    </>
  );
}